import { z } from 'zod/v4';

import { chainKeySchema, type ChainKey } from '@infinex/chain-config';

import type { MarginMode } from './common';
import type {
  CapabilityDecision,
  Freshness,
  MarketIdentity,
  MarketProductKind,
  PerpsProviderId,
  ProviderCapabilityName,
  SupportIdentifier,
} from './providerRegistry';
import {
  capabilityDecisionSchema,
  freshnessSchema,
  marketIdentitySchema,
  marketProductKindSchema,
  perpsProviderIdSchema,
  supportIdentifierSchema,
} from './providerRegistry';

import type {
  ExternalAppActionStatus,
  ExternalAppActionType,
} from '../../external-app-action';

export const ORDER_INTENT_SIDES = ['buy', 'sell'] as const;

export const ORDER_INTENT_KINDS = [
  'market',
  'limit',
  'stop_market',
  'stop_limit',
] as const;

export const ORDER_INTENT_TIME_IN_FORCE = [
  'gtc',
  'ioc',
  'alo',
  'frontend_market',
] as const;

export const ORDER_INTENT_SOURCES = [
  'order_entry',
  'close',
  'reverse',
  'scale',
  'twap',
  'tpsl',
] as const;

export const ORDER_INTENT_CAPABILITY_KINDS = [
  'base_order',
  'trigger_order',
  'post_only',
  'reduce_only',
  'tp_sl',
] as const;

export const RISK_INTENT_KINDS = [
  'close_position',
  'reverse_position',
  'modify_tpsl',
  'scale_order',
  'twap_order',
] as const;

export const ADVANCED_ORDER_CHILD_STATUSES = [
  'planned',
  'submitted',
  'filled',
  'cancelled',
  'failed',
  'indeterminate',
] as const;

export const PORT_FAILURE_KINDS = [
  'provider_rejected',
  'transport_error',
  'timeout',
  'indeterminate',
  'unsupported',
  'parse_error',
] as const;

export const PROVIDER_STATUSES = [
  'ready',
  'empty',
  'unsupported',
  'stale',
  'failed',
] as const;

export const READ_MODEL_DATA_STATES = [
  ...PROVIDER_STATUSES,
  'fixture_gated',
] as const;

export const BALANCE_KINDS = [
  'perp_margin',
  'spot_token',
  'outcome_token',
] as const;

export const PROVIDER_BALANCE_CLASSES = ['perp', 'spot'] as const;

export const PROVIDER_INTERNAL_TRANSFER_DIRECTIONS = [
  'perp_to_spot',
  'spot_to_perp',
] as const;

export const POSITION_SIDES = ['long', 'short'] as const;

export const PERPS_ACTIVITY_TYPES = [
  'order',
  'fill',
  'deposit',
  'withdraw',
  'internal_transfer',
  'advanced_order_parent',
  'external_action_review',
] as const;

export const EXTERNAL_ACTION_STATES = [
  'created',
  'pending',
  'succeeded',
  'failed',
  'requires_review',
] as const;

export const EXTERNAL_APP_ACTION_STATUS_STATE_MAP = {
  pending: 'pending',
  executing: 'pending',
  submitted: 'pending',
  success: 'succeeded',
  error: 'failed',
  requires_review: 'requires_review',
} as const satisfies Record<ExternalAppActionStatus, ExternalActionState>;

export const EXTERNAL_ACTION_KINDS = [
  'deposit',
  'deposit_with_swidge',
  'withdraw',
  'usd_class_transfer',
  'risk_action',
] as const;

export const REQUIRES_REVIEW_REASONS = [
  'indeterminate_provider_action',
  'risk_lifecycle_indeterminate',
  'insufficient_provider_evidence',
] as const;

export const AUTOMATED_REVIEW_RESULTS = [
  'resolved_success',
  'resolved_failure',
  'needs_manual_support',
  'retry_recommended',
  'insufficient_evidence',
] as const;

export type MarketIdentityForProduct<TProductKind extends MarketProductKind> =
  MarketIdentity & {
    productKind: TProductKind;
  };

export type OrderIntentSide = (typeof ORDER_INTENT_SIDES)[number];

export type OrderIntentKind = (typeof ORDER_INTENT_KINDS)[number];

export type OrderIntentTimeInForce =
  (typeof ORDER_INTENT_TIME_IN_FORCE)[number];

export type OrderIntentSource = (typeof ORDER_INTENT_SOURCES)[number];

export type OrderIntentCapabilityKind =
  (typeof ORDER_INTENT_CAPABILITY_KINDS)[number];

export type OrderIntentCapabilityRequirement = {
  kind: OrderIntentCapabilityKind;
  capability: ProviderCapabilityName;
  productKind: MarketProductKind;
  decision?: CapabilityDecision;
};

type OrderIntentBase = {
  side: OrderIntentSide;
  kind: OrderIntentKind;
  size: string;
  price?: string;
  triggerPrice?: string;
  timeInForce?: OrderIntentTimeInForce;
  clientOrderId?: string;
  capabilityRequirements?: readonly OrderIntentCapabilityRequirement[];
};

export type PerpOrderIntent = OrderIntentBase & {
  market: MarketIdentityForProduct<'perp'>;
  reduceOnly?: boolean;
  source: OrderIntentSource;
};

export type SpotOrderIntent = OrderIntentBase & {
  market: MarketIdentityForProduct<'spot'>;
  reduceOnly?: never;
  source: 'order_entry';
};

export type OutcomeOrderIntent = OrderIntentBase & {
  market: MarketIdentityForProduct<'outcome'>;
  reduceOnly?: never;
  source: 'order_entry';
};

export type OrderIntent =
  | PerpOrderIntent
  | SpotOrderIntent
  | OutcomeOrderIntent;

export type RiskLifecycleFailPolicy = 'fail_closed';

export type RiskLifecycleIndeterminatePolicy = 'requires_review';

export type AdvancedOrderOwnership = 'app_orchestrated' | 'provider_native';

export type RiskIntent =
  | {
      kind: 'close_position';
      market: MarketIdentityForProduct<'perp'>;
      size?: string;
      failPolicy: RiskLifecycleFailPolicy;
    }
  | {
      kind: 'reverse_position';
      market: MarketIdentityForProduct<'perp'>;
      currentPositionSize: string;
      cancelProtection: 'required';
      indeterminatePolicy: RiskLifecycleIndeterminatePolicy;
    }
  | {
      kind: 'modify_tpsl';
      market: MarketIdentityForProduct<'perp'>;
      takeProfitPrice?: string;
      stopLossPrice?: string;
      indeterminatePolicy: RiskLifecycleIndeterminatePolicy;
    }
  | {
      kind: 'scale_order' | 'twap_order';
      market: MarketIdentityForProduct<'perp'>;
      parentClientOrderId: string;
      childPolicy: AdvancedOrderOwnership;
    };

export type IntentValidationFailureReason =
  | 'capability_unsupported'
  | 'insufficient_base_balance'
  | 'insufficient_quote_balance'
  | 'invalid_size'
  | 'invalid_price'
  | 'invalid_precision'
  | 'market_not_ready'
  | 'non_usdc_quote_execution_not_exposed'
  | 'reduce_only_not_applicable_to_spot'
  | 'provider_not_ready';

export type IntentValidationResult =
  | { ok: true; normalized: OrderIntent | RiskIntent }
  | {
      ok: false;
      reason: IntentValidationFailureReason;
      capability?: CapabilityDecision;
    };

export type PerpsIntentValidator = {
  validateOrder(intent: OrderIntent): IntentValidationResult;
  validateRisk(intent: RiskIntent): IntentValidationResult;
  normalizeOrder(input: unknown): IntentValidationResult;
  normalizeRisk(input: unknown): IntentValidationResult;
};

export type AdvancedOrderChildStatus =
  (typeof ADVANCED_ORDER_CHILD_STATUSES)[number];

export type AdvancedOrderLifecycle = {
  parentClientOrderId: string;
  providerParentOrderId?: string;
  providerId: PerpsProviderId;
  market: MarketIdentityForProduct<'perp'>;
  kind: 'scale' | 'twap';
  ownership: AdvancedOrderOwnership;
  children: readonly {
    clientOrderId?: string;
    providerOrderId?: string;
    status: AdvancedOrderChildStatus;
  }[];
};

export type PortFailureKind = (typeof PORT_FAILURE_KINDS)[number];

export type PortFailure = {
  kind: PortFailureKind;
  message: string;
  providerRequestId?: string;
  retryable?: boolean;
  evidence?: Readonly<Record<string, unknown>>;
};

export type Result<TValue, TFailure = PortFailure> =
  | { ok: true; value: TValue }
  | { ok: false; failure: TFailure };

export type PortResult<TValue> = Result<TValue, PortFailure> & {
  raw?: unknown;
};

export type ProviderOrderResult = {
  providerOrderId?: string;
  clientOrderId?: string;
  status: 'accepted' | 'filled' | 'resting' | 'rejected' | 'indeterminate';
};

export type ProviderCancelInput = {
  market: MarketIdentity;
  providerOrderId?: string;
  clientOrderId?: string;
};

export type ProviderCancelResult = {
  status: 'cancelled' | 'not_found' | 'rejected' | 'indeterminate';
};

export type ProviderModifyInput = {
  market: MarketIdentity;
  providerOrderId?: string;
  clientOrderId?: string;
  replacement: OrderIntent;
};

export type ProviderWithdrawInput = {
  providerId: PerpsProviderId;
  accountId: string;
  asset: string;
  amountAtomic: string;
  destinationAddress: string;
  chainId?: number;
};

export type ProviderWithdrawResult = {
  status: 'submitted' | 'succeeded' | 'failed' | 'indeterminate';
  providerRequestId?: string;
  txHash?: string;
};

export type ProviderBalanceClass = (typeof PROVIDER_BALANCE_CLASSES)[number];

export type ProviderInternalTransferDirection =
  (typeof PROVIDER_INTERNAL_TRANSFER_DIRECTIONS)[number];

export type ProviderInternalClassTransferReconciliationEvidence = {
  before?: {
    fromAmount?: string;
    toAmount?: string;
  };
  after?: {
    fromAmount?: string;
    toAmount?: string;
  };
  observedAt?: string;
};

export type InternalProviderMovement = {
  kind: 'provider_internal_class_transfer';
  providerId: PerpsProviderId;
  accountId: string;
  asset: 'USDC';
  amount: string;
  fromClass: ProviderBalanceClass;
  toClass: ProviderBalanceClass;
  providerRequestId?: string;
  providerActionHash?: string;
  nonce?: number;
  reconciliation?: ProviderInternalClassTransferReconciliationEvidence;
};

export type ProviderInternalClassTransferMetadata = InternalProviderMovement;

export type ProviderUsdClassTransferInput = {
  providerId: PerpsProviderId;
  accountId: string;
  asset: 'USDC';
  amount: string;
  fromClass: ProviderBalanceClass;
  toClass: ProviderBalanceClass;
};

export type ProviderUsdClassTransferResult = {
  status:
    | 'submitted'
    | 'succeeded'
    | 'failed'
    | 'indeterminate'
    | 'requires_review';
  providerRequestId?: string;
  providerActionHash?: string;
  nonce?: number;
  providerStatusId?: string;
  supportSafeError?: ReviewEvidence['supportSafeError'];
  internalMovement: InternalProviderMovement;
};

export type ProviderAssetIdentity = {
  providerId: PerpsProviderId;
  productKind: MarketProductKind;
  raw: unknown;
};

export type ExchangeProtocolPort = {
  providerId: PerpsProviderId;
  placeOrder(intent: OrderIntent): Promise<PortResult<ProviderOrderResult>>;
  cancelOrder(
    input: ProviderCancelInput
  ): Promise<PortResult<ProviderCancelResult>>;
  modifyOrder(
    input: ProviderModifyInput
  ): Promise<PortResult<ProviderOrderResult>>;
  withdraw(
    input: ProviderWithdrawInput
  ): Promise<PortResult<ProviderWithdrawResult>>;
  usdClassTransfer(
    input: ProviderUsdClassTransferInput
  ): Promise<PortResult<ProviderUsdClassTransferResult>>;
  updateLeverage?(
    input: ProviderLeverageUpdateInput
  ): Promise<PortResult<unknown>>;
  updateMarginMode?(
    input: ProviderMarginModeUpdateInput
  ): Promise<PortResult<unknown>>;
  updateIsolatedMargin?(
    input: ProviderIsolatedMarginUpdateInput
  ): Promise<PortResult<unknown>>;
  translateAssetIdentity(identity: MarketIdentity): ProviderAssetIdentity;
  parseProviderResponse(raw: unknown): PortResult<unknown>;
};

export type ProviderLeverageUpdateInput = {
  market: MarketIdentity;
  leverage: number;
  marginMode?: MarginMode;
};

export type ProviderMarginModeUpdateInput = {
  market: MarketIdentity;
  marginMode: MarginMode;
  leverage?: number;
};

export type ProviderIsolatedMarginUpdateInput = {
  market: MarketIdentity;
  isBuy: boolean;
  ntli: number;
};

export type AccountEpoch = {
  providerId: PerpsProviderId;
  accountId: string;
  generation: number;
  createdAt: string;
};

export type ProviderStatus = (typeof PROVIDER_STATUSES)[number];

export type ReadModelDataState = (typeof READ_MODEL_DATA_STATES)[number];

export type ReadModelSupportIdentifiers = Readonly<
  Record<string, string | number | boolean>
>;

export type ReadModelResult<TData> =
  | {
      state: 'ready';
      data: TData;
      freshness?: AccountSnapshot['freshness'];
    }
  | {
      state: 'empty';
      freshness?: AccountSnapshot['freshness'];
    }
  | {
      state: 'stale';
      data: TData;
      freshness: AccountSnapshot['freshness'];
      error: NonNullable<AccountSnapshot['error']>;
    }
  | {
      state: 'failed';
      error: NonNullable<AccountSnapshot['error']>;
      freshness?: AccountSnapshot['freshness'];
    }
  | {
      state: 'unsupported';
      capability?: CapabilityDecision;
      reason: string;
    }
  | {
      state: 'fixture_gated';
      capability?: CapabilityDecision;
      fixture?: string;
      reason: string;
    };

export type PerpsRealtimeEvent =
  | {
      type: 'account.snapshot';
      generation: AccountEpoch;
      snapshot: AccountSnapshot;
    }
  | {
      type: 'account.patch';
      generation: AccountEpoch;
      patch: Partial<AccountSnapshot>;
    }
  | { type: 'account.clear'; generation: AccountEpoch; reason: string }
  | {
      type: 'account.fill';
      generation: AccountEpoch;
      fill: PerpsActivityItem;
    }
  | {
      type: 'provider.status';
      providerId: PerpsProviderId;
      status: ProviderStatus;
      error?: string;
    }
  | {
      type: 'market.price';
      market: MarketIdentity;
      price: string;
      observedAt: string;
    }
  | { type: 'token.balance'; accountId: string; balance: PerpsBalance };

export type RealtimeSubscription = {
  unsubscribe(): Promise<void>;
  generation: AccountEpoch;
};

export type RealtimeStreamPort = {
  subscribe(input: {
    providerId: PerpsProviderId;
    accountId: string;
    markets: readonly MarketIdentity[];
  }): Promise<RealtimeSubscription>;
};

export type AccountSnapshotPort = {
  getSnapshot(input: {
    providerId: PerpsProviderId;
    accountId: string;
    generation?: number;
  }): Promise<PortResult<AccountSnapshot>>;
};

export type AccountSnapshot = {
  providerId: PerpsProviderId;
  accountId: string;
  productKind?: MarketProductKind;
  status: ProviderStatus;
  freshness: Freshness;
  error?: {
    code: string;
    message: string;
    retryable?: boolean;
  };
  balances: readonly PerpsBalance[];
  positions: readonly PerpsReadModelPosition[];
  activity: readonly PerpsActivityItem[];
};

export type BalanceKind = (typeof BALANCE_KINDS)[number];

export type PerpsBalance = {
  providerId: PerpsProviderId;
  accountId: string;
  productKind: MarketProductKind;
  balanceKind: BalanceKind;
  asset: string;
  amount: string;
  available?: string;
  hold?: string;
  /**
   * Cost basis of the holding in quote-currency terms, when the provider
   * tracks one. For Hyperliquid spot this is the `entryNtl` (entry notional)
   * — the cumulative USDC paid to acquire the current balance, net of sells.
   */
  entryNotional?: string;
  /**
   * Quote-currency (USD/USDC) mark price per unit at dataset build time,
   * sourced from the provider's market feed. Populated for spot token
   * balances when a price was available; absent rows are unpriced and must
   * not be silently valued at $0. USDC rows carry '1'.
   *
   * Freshness is portfolio-grade, not tick-grade: the price refreshes with
   * the dataset rebuild (~15s) from a worker-side market fetch cached ~60s
   * (`getHyperliquidSpotPriceLookup`). Surfaces comparing against the
   * perps-app — which prices off its live websocket feed — should expect
   * agreement within price-tick tolerance, not to the cent. The perps-app
   * remains the trading-grade source.
   */
  markPrice?: string;
  tokenIndex?: number;
  tokenId?: string;
  quoteToken?: string;
  /**
   * Chain used to route deposits/withdrawals for this provider's collateral,
   * when applicable. Populated for perp margin balances from the provider's
   * clearing-house configuration.
   */
  chain?: ChainKey;
  /** Collateral asset (token) address for deposit/withdrawal routing, when applicable. */
  assetAddress?: string;
  status: ProviderStatus;
  supportIdentifiers?: readonly SupportIdentifier[];
};

export type PerpsBalancesProviderStatus = {
  providerId: PerpsProviderId;
  accountId: string;
  status: ProviderStatus;
  freshness: AccountSnapshot['freshness'];
  error?: AccountSnapshot['error'];
};

export type PerpsBalancesSnapshot = {
  balances: readonly PerpsBalance[];
  providers: readonly PerpsBalancesProviderStatus[];
};

export type PerpsReadModelPosition = {
  market: MarketIdentityForProduct<'perp'>;
  side: 'long' | 'short';
  size: string;
  entryPrice?: string;
  liquidationPrice?: string;
  marginMode?: 'cross' | 'isolated';
};

export type PerpsActivityType = (typeof PERPS_ACTIVITY_TYPES)[number];

export type ExternalActionState = (typeof EXTERNAL_ACTION_STATES)[number];

export type PerpsActivityIdentity = {
  providerId: PerpsProviderId;
  accountId: string;
  productKind: MarketProductKind;
  providerMarketId?: string;
  providerOrderId?: string;
  providerTradeId?: string;
  providerActionId?: string;
  clientOrderId?: string;
  parentClientOrderId?: string;
};

export type PerpFillActivity = {
  kind: 'perp_fill';
  providerOrderId?: string;
  providerTradeId?: string;
  side?: string;
  direction?: string;
  size?: string;
  price?: string;
  fee?: string;
  closedPnl?: string;
  hash?: string;
  twapId?: string;
  builderFee?: string;
};

export type SpotFillActivity = {
  kind: 'spot_fill';
  providerOrderId?: string;
  providerTradeId?: string;
  /** Provider order-book side: 'B' (buy) or 'A' (sell). */
  side?: string;
  baseAsset?: string;
  quoteAsset?: string;
  size?: string;
  price?: string;
  fee?: string;
  feeToken?: string;
};

export type OutcomeFillActivity = {
  kind: 'outcome_fill';
  providerOrderId?: string;
  providerTradeId?: string;
  outcomeSide?: string;
  size?: string;
  price?: string;
  fee?: string;
};

export type PerpsActivityFill =
  | PerpFillActivity
  | SpotFillActivity
  | OutcomeFillActivity;

export type PerpsActivityAdvancedOrder = {
  kind: AdvancedOrderLifecycle['kind'];
  parentClientOrderId: string;
  providerParentOrderId?: string;
  ownership: AdvancedOrderOwnership;
  children: AdvancedOrderLifecycle['children'];
};

export type ProviderInternalClassTransferActivity = InternalProviderMovement;

export type PerpsActivityInternalMovement =
  ProviderInternalClassTransferActivity;

export type PerpsActivityItem = {
  id: string;
  providerId: PerpsProviderId;
  accountId: string;
  productKind: MarketProductKind;
  identity: PerpsActivityIdentity;
  market?: MarketIdentity;
  type: PerpsActivityType;
  fill?: PerpsActivityFill;
  advancedOrder?: PerpsActivityAdvancedOrder;
  internalMovement?: PerpsActivityInternalMovement;
  /** Present for deposit/withdraw/usd_class_transfer external actions. */
  externalAction?: {
    action: ExternalAppActionType;
    amountUsd?: string;
    asset?: string;
  };
  attribution: {
    providerAccountId?: string;
    infinexUserId?: string;
    rewardUserId?: string;
    legacyUnknown?: boolean;
  };
  status: ProviderStatus | ExternalActionState;
  observedAt: string;
};

export type HistoryQuery = {
  accountId: string;
  providerId?: PerpsProviderId;
  productKind?: MarketProductKind;
  cursor?: string;
  limit: number;
};

export type HistoryPage<TItem> = {
  items: readonly TItem[];
  nextCursor?: string;
  previousCursor?: string;
};

export type PerpsTopOfBook = {
  bid?: {
    price: string;
    size?: string;
  };
  ask?: {
    price: string;
    size?: string;
  };
  observedAt?: string;
};

export type PerpsMarketDataSnapshot = {
  market: MarketIdentity;
  mid: ReadModelResult<string>;
  orderBook: ReadModelResult<unknown>;
  recentTrades: ReadModelResult<readonly unknown[]>;
  candles: ReadModelResult<readonly unknown[]>;
  topOfBook: ReadModelResult<PerpsTopOfBook>;
};

export type PerpsReadModel = {
  getAccount(input: {
    providerId: PerpsProviderId;
    accountId: string;
    productKind?: MarketProductKind;
  }): Promise<AccountSnapshot>;
  getBalances(input: {
    accountId: string;
    providers?: readonly PerpsProviderId[];
    productKind?: MarketProductKind;
  }): Promise<PerpsBalancesSnapshot>;
  getActivity(input: HistoryQuery): Promise<HistoryPage<PerpsActivityItem>>;
  getHistory(input: HistoryQuery): Promise<HistoryPage<PerpsActivityItem>>;
  getMarketData?(input: {
    market: MarketIdentity;
  }): Promise<PerpsMarketDataSnapshot>;
};

export type ReviewEvidence = {
  actionId: string;
  providerId: PerpsProviderId;
  accountId: string;
  chainId?: number;
  txHash?: string;
  providerRequestId?: string;
  amount?: string;
  asset?: string;
  timestamps: readonly string[];
  retryCount: number;
  lastProviderStatus?: string;
  errorOrTimeoutContext?: string;
  internalMovement?: InternalProviderMovement;
  supportSafeError?: {
    code?: string;
    message: string;
    providerStatus?: string;
  };
};

export type RequiresReviewReason = (typeof REQUIRES_REVIEW_REASONS)[number];

export type RequiresReviewState = {
  state: 'requires_review';
  reason: RequiresReviewReason;
  evidence?: ReviewEvidence;
  failure?: PortFailure;
};

export type ExternalActionKind = (typeof EXTERNAL_ACTION_KINDS)[number];

export type ExternalActionRecord = {
  id: string;
  providerId: PerpsProviderId;
  accountId: string;
  action: ExternalActionKind;
  state: ExternalActionState;
  amount?: string;
  asset?: string;
  chainId?: number;
  providerRequestId?: string;
  txHash?: string;
  authorizationData?: unknown;
  evidence?: ReviewEvidence;
  createdAt: string;
  updatedAt: string;
};

export type AutomatedReviewResultKind =
  (typeof AUTOMATED_REVIEW_RESULTS)[number];

export type AutomatedReviewResult =
  | { result: 'resolved_success'; evidence: ReviewEvidence }
  | { result: 'resolved_failure'; evidence: ReviewEvidence }
  | { result: 'needs_manual_support'; evidence: ReviewEvidence }
  | { result: 'retry_recommended'; evidence: ReviewEvidence }
  | { result: 'insufficient_evidence'; evidence: ReviewEvidence };

export type ExternalActionPort = {
  create(record: ExternalActionRecord): Promise<ExternalActionRecord>;
  transition(
    id: string,
    state: ExternalActionState,
    evidence?: ReviewEvidence
  ): Promise<ExternalActionRecord>;
  list(input: {
    accountId: string;
    providerId?: PerpsProviderId;
  }): Promise<readonly ExternalActionRecord[]>;
};

export type ExternalActionReviewPort = {
  review(action: ExternalActionRecord): Promise<AutomatedReviewResult>;
};

export type PerpsDepositInput = {
  providerId: PerpsProviderId;
  asset: string;
  amountAtomic: string;
  fromChain?: number;
};

export type PerpsDepositWithSwidgeInput = PerpsDepositInput & {
  sourceAsset: string;
  sourceChain: number;
};

export type PerpsWithdrawInput = {
  providerId: PerpsProviderId;
  asset: string;
  amountAtomic: string;
  destinationAddress: string;
  fromChain?: number;
};

export type PerpsUsdClassTransferInput = {
  providerId: 'hyperliquid';
  accountId: string;
  asset: 'USDC';
  amount: string;
  fromClass: ProviderBalanceClass;
  toClass: ProviderBalanceClass;
};

export type UsdClassTransferInput = PerpsUsdClassTransferInput;

export type UsdClassTransferActionRecord = {
  id: string;
  providerId: 'hyperliquid';
  accountId: string;
  action: 'usd_class_transfer';
  state: ExternalActionState;
  amount: string;
  asset: 'USDC';
  internalMovement: InternalProviderMovement;
  providerRequestId?: string;
  txHash?: string;
  evidence?: ReviewEvidence;
  createdAt: string;
  updatedAt: string;
};

export type PerpsActionOutput = {
  actionId: string;
  state: ExternalActionState;
  providerId: PerpsProviderId;
  supportReference?: string;
};

const perpMarketIdentitySchema = marketIdentitySchema.extend({
  productKind: z.literal('perp'),
}) satisfies z.ZodType<MarketIdentityForProduct<'perp'>>;

const spotMarketIdentitySchema = marketIdentitySchema.extend({
  productKind: z.literal('spot'),
}) satisfies z.ZodType<MarketIdentityForProduct<'spot'>>;

const outcomeMarketIdentitySchema = marketIdentitySchema.extend({
  productKind: z.literal('outcome'),
}) satisfies z.ZodType<MarketIdentityForProduct<'outcome'>>;

export const orderIntentCapabilityRequirementSchema = z.object({
  kind: z.enum(ORDER_INTENT_CAPABILITY_KINDS),
  capability: z.enum([
    'marketOrder',
    'limitOrder',
    'triggerOrder',
    'tpSl',
    'reduceOnly',
    'postOnly',
  ]),
  productKind: marketProductKindSchema,
  decision: capabilityDecisionSchema.optional(),
}) satisfies z.ZodType<OrderIntentCapabilityRequirement>;

const orderIntentBaseSchema = z.object({
  side: z.enum(ORDER_INTENT_SIDES),
  kind: z.enum(ORDER_INTENT_KINDS),
  size: z.string(),
  price: z.string().optional(),
  triggerPrice: z.string().optional(),
  timeInForce: z.enum(ORDER_INTENT_TIME_IN_FORCE).optional(),
  clientOrderId: z.string().optional(),
  capabilityRequirements: z
    .array(orderIntentCapabilityRequirementSchema)
    .optional(),
});

export const perpOrderIntentSchema = orderIntentBaseSchema.extend({
  market: perpMarketIdentitySchema,
  reduceOnly: z.boolean().optional(),
  source: z.enum(ORDER_INTENT_SOURCES),
}) satisfies z.ZodType<PerpOrderIntent>;

export const spotOrderIntentSchema = orderIntentBaseSchema.extend({
  market: spotMarketIdentitySchema,
  reduceOnly: z.never().optional(),
  source: z.literal('order_entry'),
}) satisfies z.ZodType<SpotOrderIntent>;

export const outcomeOrderIntentSchema = orderIntentBaseSchema.extend({
  market: outcomeMarketIdentitySchema,
  reduceOnly: z.never().optional(),
  source: z.literal('order_entry'),
}) satisfies z.ZodType<OutcomeOrderIntent>;

export const orderIntentSchema = z.union([
  perpOrderIntentSchema,
  spotOrderIntentSchema,
  outcomeOrderIntentSchema,
]) satisfies z.ZodType<OrderIntent>;

export const riskIntentSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('close_position'),
    market: perpMarketIdentitySchema,
    size: z.string().optional(),
    failPolicy: z.literal('fail_closed'),
  }),
  z.object({
    kind: z.literal('reverse_position'),
    market: perpMarketIdentitySchema,
    currentPositionSize: z.string(),
    cancelProtection: z.literal('required'),
    indeterminatePolicy: z.literal('requires_review'),
  }),
  z.object({
    kind: z.literal('modify_tpsl'),
    market: perpMarketIdentitySchema,
    takeProfitPrice: z.string().optional(),
    stopLossPrice: z.string().optional(),
    indeterminatePolicy: z.literal('requires_review'),
  }),
  z.object({
    kind: z.literal('scale_order'),
    market: perpMarketIdentitySchema,
    parentClientOrderId: z.string(),
    childPolicy: z.enum(['app_orchestrated', 'provider_native']),
  }),
  z.object({
    kind: z.literal('twap_order'),
    market: perpMarketIdentitySchema,
    parentClientOrderId: z.string(),
    childPolicy: z.enum(['app_orchestrated', 'provider_native']),
  }),
]) satisfies z.ZodType<RiskIntent>;

export const intentValidationResultSchema = z.union([
  z.object({
    ok: z.literal(true),
    normalized: z.union([orderIntentSchema, riskIntentSchema]),
  }),
  z.object({
    ok: z.literal(false),
    reason: z.enum([
      'capability_unsupported',
      'insufficient_base_balance',
      'insufficient_quote_balance',
      'invalid_size',
      'invalid_price',
      'invalid_precision',
      'market_not_ready',
      'non_usdc_quote_execution_not_exposed',
      'reduce_only_not_applicable_to_spot',
      'provider_not_ready',
    ]),
    capability: capabilityDecisionSchema.optional(),
  }),
]) satisfies z.ZodType<IntentValidationResult>;

export const advancedOrderLifecycleSchema = z.object({
  parentClientOrderId: z.string(),
  providerParentOrderId: z.string().optional(),
  providerId: perpsProviderIdSchema,
  market: perpMarketIdentitySchema,
  kind: z.enum(['scale', 'twap']),
  ownership: z.enum(['app_orchestrated', 'provider_native']),
  children: z.array(
    z.object({
      clientOrderId: z.string().optional(),
      providerOrderId: z.string().optional(),
      status: z.enum(ADVANCED_ORDER_CHILD_STATUSES),
    })
  ),
}) satisfies z.ZodType<AdvancedOrderLifecycle>;

export const portFailureSchema = z.object({
  kind: z.enum(PORT_FAILURE_KINDS),
  message: z.string(),
  providerRequestId: z.string().optional(),
  retryable: z.boolean().optional(),
  evidence: z.record(z.string(), z.unknown()).optional(),
}) satisfies z.ZodType<PortFailure>;

export const resultSchema = <TValue, TFailure = PortFailure>(
  valueSchema: z.ZodType<TValue>,
  failureSchema: z.ZodType<TFailure> = portFailureSchema as unknown as z.ZodType<TFailure>
) =>
  z.discriminatedUnion('ok', [
    z.object({ ok: z.literal(true), value: valueSchema }),
    z.object({ ok: z.literal(false), failure: failureSchema }),
  ]) satisfies z.ZodType<Result<TValue, TFailure>>;

export const portResultSchema = <TValue>(valueSchema: z.ZodType<TValue>) =>
  z.union([
    z.object({
      ok: z.literal(true),
      value: valueSchema,
      raw: z.unknown().optional(),
    }),
    z.object({
      ok: z.literal(false),
      failure: portFailureSchema,
      raw: z.unknown().optional(),
    }),
  ]) satisfies z.ZodType<PortResult<TValue>>;

export const providerOrderResultSchema = z.object({
  providerOrderId: z.string().optional(),
  clientOrderId: z.string().optional(),
  status: z.enum([
    'accepted',
    'filled',
    'resting',
    'rejected',
    'indeterminate',
  ]),
}) satisfies z.ZodType<ProviderOrderResult>;

export const providerCancelInputSchema = z.object({
  market: marketIdentitySchema,
  providerOrderId: z.string().optional(),
  clientOrderId: z.string().optional(),
}) satisfies z.ZodType<ProviderCancelInput>;

export const providerCancelResultSchema = z.object({
  status: z.enum(['cancelled', 'not_found', 'rejected', 'indeterminate']),
}) satisfies z.ZodType<ProviderCancelResult>;

export const providerModifyInputSchema = z.object({
  market: marketIdentitySchema,
  providerOrderId: z.string().optional(),
  clientOrderId: z.string().optional(),
  replacement: orderIntentSchema,
}) satisfies z.ZodType<ProviderModifyInput>;

export const providerWithdrawInputSchema = z.object({
  providerId: perpsProviderIdSchema,
  accountId: z.string(),
  asset: z.string(),
  amountAtomic: z.string(),
  destinationAddress: z.string(),
  chainId: z.number().int().positive().optional(),
}) satisfies z.ZodType<ProviderWithdrawInput>;

export const providerWithdrawResultSchema = z.object({
  status: z.enum(['submitted', 'succeeded', 'failed', 'indeterminate']),
  providerRequestId: z.string().optional(),
  txHash: z.string().optional(),
}) satisfies z.ZodType<ProviderWithdrawResult>;

export const providerBalanceClassSchema = z.enum(PROVIDER_BALANCE_CLASSES);

export const providerInternalTransferDirectionSchema = z.enum(
  PROVIDER_INTERNAL_TRANSFER_DIRECTIONS
);

export const providerInternalClassTransferReconciliationEvidenceSchema =
  z.object({
    before: z
      .object({
        fromAmount: z.string().optional(),
        toAmount: z.string().optional(),
      })
      .optional(),
    after: z
      .object({
        fromAmount: z.string().optional(),
        toAmount: z.string().optional(),
      })
      .optional(),
    observedAt: z.string().optional(),
  }) satisfies z.ZodType<ProviderInternalClassTransferReconciliationEvidence>;

export const internalProviderMovementSchema = z.object({
  kind: z.literal('provider_internal_class_transfer'),
  providerId: perpsProviderIdSchema,
  accountId: z.string(),
  asset: z.literal('USDC'),
  amount: z.string(),
  fromClass: providerBalanceClassSchema,
  toClass: providerBalanceClassSchema,
  providerRequestId: z.string().optional(),
  providerActionHash: z.string().optional(),
  nonce: z.number().int().nonnegative().optional(),
  reconciliation:
    providerInternalClassTransferReconciliationEvidenceSchema.optional(),
}) satisfies z.ZodType<InternalProviderMovement>;

export const providerInternalClassTransferMetadataSchema =
  internalProviderMovementSchema satisfies z.ZodType<ProviderInternalClassTransferMetadata>;

export const providerUsdClassTransferInputSchema = z.object({
  providerId: perpsProviderIdSchema,
  accountId: z.string(),
  asset: z.literal('USDC'),
  amount: z.string(),
  fromClass: providerBalanceClassSchema,
  toClass: providerBalanceClassSchema,
}) satisfies z.ZodType<ProviderUsdClassTransferInput>;

const reviewEvidenceSupportSafeErrorSchema = z.object({
  code: z.string().optional(),
  message: z.string(),
  providerStatus: z.string().optional(),
});

export const providerUsdClassTransferResultSchema = z.object({
  status: z.enum([
    'submitted',
    'succeeded',
    'failed',
    'indeterminate',
    'requires_review',
  ]),
  providerRequestId: z.string().optional(),
  providerActionHash: z.string().optional(),
  nonce: z.number().int().nonnegative().optional(),
  providerStatusId: z.string().optional(),
  supportSafeError: reviewEvidenceSupportSafeErrorSchema.optional(),
  internalMovement: internalProviderMovementSchema,
}) satisfies z.ZodType<ProviderUsdClassTransferResult>;

export const providerAssetIdentitySchema = z.object({
  providerId: perpsProviderIdSchema,
  productKind: marketProductKindSchema,
  raw: z.unknown(),
}) satisfies z.ZodType<ProviderAssetIdentity>;

export const accountEpochSchema = z.object({
  providerId: perpsProviderIdSchema,
  accountId: z.string(),
  generation: z.number().int().nonnegative(),
  createdAt: z.string(),
}) satisfies z.ZodType<AccountEpoch>;

export const providerStatusSchema = z.enum(PROVIDER_STATUSES);

export const readModelDataStateSchema = z.enum(READ_MODEL_DATA_STATES);

export const perpsBalanceSchema = z.object({
  providerId: perpsProviderIdSchema,
  accountId: z.string(),
  productKind: marketProductKindSchema,
  balanceKind: z.enum(BALANCE_KINDS),
  asset: z.string(),
  amount: z.string(),
  available: z.string().optional(),
  hold: z.string().optional(),
  entryNotional: z.string().optional(),
  markPrice: z.string().optional(),
  tokenIndex: z.number().int().nonnegative().optional(),
  tokenId: z.string().optional(),
  quoteToken: z.string().optional(),
  chain: chainKeySchema.optional(),
  assetAddress: z.string().optional(),
  status: providerStatusSchema,
  supportIdentifiers: z.array(supportIdentifierSchema).readonly().optional(),
}) satisfies z.ZodType<PerpsBalance>;

export const perpsBalancesProviderStatusSchema = z.object({
  providerId: perpsProviderIdSchema,
  accountId: z.string(),
  status: providerStatusSchema,
  freshness: z.object({
    observedAt: z.string().optional(),
    staleSince: z.string().optional(),
    generation: z.number().int().nonnegative().optional(),
  }),
  error: z
    .object({
      code: z.string(),
      message: z.string(),
      retryable: z.boolean().optional(),
    })
    .optional(),
}) satisfies z.ZodType<PerpsBalancesProviderStatus>;

export const perpsBalancesSnapshotSchema = z.object({
  balances: z.array(perpsBalanceSchema),
  providers: z.array(perpsBalancesProviderStatusSchema),
}) satisfies z.ZodType<PerpsBalancesSnapshot>;

export const perpsReadModelPositionSchema = z.object({
  market: perpMarketIdentitySchema,
  side: z.enum(POSITION_SIDES),
  size: z.string(),
  entryPrice: z.string().optional(),
  liquidationPrice: z.string().optional(),
  marginMode: z.enum(['cross', 'isolated']).optional(),
}) satisfies z.ZodType<PerpsReadModelPosition>;

export const externalActionStateSchema = z.enum(EXTERNAL_ACTION_STATES);

export const perpsActivityItemSchema = z.object({
  id: z.string(),
  providerId: perpsProviderIdSchema,
  accountId: z.string(),
  productKind: marketProductKindSchema,
  identity: z.object({
    providerId: perpsProviderIdSchema,
    accountId: z.string(),
    productKind: marketProductKindSchema,
    providerMarketId: z.string().optional(),
    providerOrderId: z.string().optional(),
    providerTradeId: z.string().optional(),
    providerActionId: z.string().optional(),
    clientOrderId: z.string().optional(),
    parentClientOrderId: z.string().optional(),
  }),
  market: marketIdentitySchema.optional(),
  type: z.enum(PERPS_ACTIVITY_TYPES),
  fill: z
    .discriminatedUnion('kind', [
      z.object({
        kind: z.literal('perp_fill'),
        providerOrderId: z.string().optional(),
        providerTradeId: z.string().optional(),
        side: z.string().optional(),
        direction: z.string().optional(),
        size: z.string().optional(),
        price: z.string().optional(),
        fee: z.string().optional(),
        closedPnl: z.string().optional(),
        hash: z.string().optional(),
        twapId: z.string().optional(),
        builderFee: z.string().optional(),
      }),
      z.object({
        kind: z.literal('spot_fill'),
        providerOrderId: z.string().optional(),
        providerTradeId: z.string().optional(),
        side: z.string().optional(),
        baseAsset: z.string().optional(),
        quoteAsset: z.string().optional(),
        size: z.string().optional(),
        price: z.string().optional(),
        fee: z.string().optional(),
        feeToken: z.string().optional(),
      }),
      z.object({
        kind: z.literal('outcome_fill'),
        providerOrderId: z.string().optional(),
        providerTradeId: z.string().optional(),
        outcomeSide: z.string().optional(),
        size: z.string().optional(),
        price: z.string().optional(),
        fee: z.string().optional(),
      }),
    ])
    .optional(),
  advancedOrder: z
    .object({
      kind: z.enum(['scale', 'twap']),
      parentClientOrderId: z.string(),
      providerParentOrderId: z.string().optional(),
      ownership: z.enum(['app_orchestrated', 'provider_native']),
      children: z.array(
        z.object({
          clientOrderId: z.string().optional(),
          providerOrderId: z.string().optional(),
          status: z.enum(ADVANCED_ORDER_CHILD_STATUSES),
        })
      ),
    })
    .optional(),
  internalMovement: internalProviderMovementSchema.optional(),
  externalAction: z
    .object({
      // Inlined rather than importing EXTERNAL_APP_ACTION_TYPES to avoid a
      // runtime import cycle with external-app-action.ts (the value would be
      // undefined during module init). The satisfies check below keeps this
      // in sync with ExternalAppActionType.
      action: z.enum(['deposit', 'withdraw', 'usd_class_transfer']),
      amountUsd: z.string().optional(),
      asset: z.string().optional(),
    })
    .optional(),
  attribution: z.object({
    providerAccountId: z.string().optional(),
    infinexUserId: z.string().optional(),
    rewardUserId: z.string().optional(),
    legacyUnknown: z.boolean().optional(),
  }),
  status: z.union([providerStatusSchema, externalActionStateSchema]),
  observedAt: z.string(),
}) satisfies z.ZodType<PerpsActivityItem>;

export const accountSnapshotSchema = z.object({
  providerId: perpsProviderIdSchema,
  accountId: z.string(),
  productKind: marketProductKindSchema.optional(),
  status: providerStatusSchema,
  freshness: freshnessSchema,
  error: z
    .object({
      code: z.string(),
      message: z.string(),
      retryable: z.boolean().optional(),
    })
    .optional(),
  balances: z.array(perpsBalanceSchema),
  positions: z.array(perpsReadModelPositionSchema),
  activity: z.array(perpsActivityItemSchema),
}) satisfies z.ZodType<AccountSnapshot>;

export const perpsRealtimeEventSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('account.snapshot'),
    generation: accountEpochSchema,
    snapshot: accountSnapshotSchema,
  }),
  z.object({
    type: z.literal('account.patch'),
    generation: accountEpochSchema,
    patch: accountSnapshotSchema.partial(),
  }),
  z.object({
    type: z.literal('account.clear'),
    generation: accountEpochSchema,
    reason: z.string(),
  }),
  z.object({
    type: z.literal('account.fill'),
    generation: accountEpochSchema,
    fill: perpsActivityItemSchema,
  }),
  z.object({
    type: z.literal('provider.status'),
    providerId: perpsProviderIdSchema,
    status: providerStatusSchema,
    error: z.string().optional(),
  }),
  z.object({
    type: z.literal('market.price'),
    market: marketIdentitySchema,
    price: z.string(),
    observedAt: z.string(),
  }),
  z.object({
    type: z.literal('token.balance'),
    accountId: z.string(),
    balance: perpsBalanceSchema,
  }),
]) satisfies z.ZodType<PerpsRealtimeEvent>;

export const historyQuerySchema = z.object({
  accountId: z.string(),
  providerId: perpsProviderIdSchema.optional(),
  productKind: marketProductKindSchema.optional(),
  cursor: z.string().optional(),
  limit: z.number().int().positive(),
}) satisfies z.ZodType<HistoryQuery>;

export const historyPageSchema = <TItem>(itemSchema: z.ZodType<TItem>) =>
  z.object({
    items: z.array(itemSchema),
    nextCursor: z.string().optional(),
    previousCursor: z.string().optional(),
  }) satisfies z.ZodType<HistoryPage<TItem>>;

export const reviewEvidenceSchema = z.object({
  actionId: z.string(),
  providerId: perpsProviderIdSchema,
  accountId: z.string(),
  chainId: z.number().int().positive().optional(),
  txHash: z.string().optional(),
  providerRequestId: z.string().optional(),
  amount: z.string().optional(),
  asset: z.string().optional(),
  timestamps: z.array(z.string()),
  retryCount: z.number().int().nonnegative(),
  lastProviderStatus: z.string().optional(),
  errorOrTimeoutContext: z.string().optional(),
  internalMovement: internalProviderMovementSchema.optional(),
  supportSafeError: reviewEvidenceSupportSafeErrorSchema.optional(),
}) satisfies z.ZodType<ReviewEvidence>;

export const requiresReviewStateSchema = z.object({
  state: z.literal('requires_review'),
  reason: z.enum(REQUIRES_REVIEW_REASONS),
  evidence: reviewEvidenceSchema.optional(),
  failure: portFailureSchema.optional(),
}) satisfies z.ZodType<RequiresReviewState>;

export const externalActionRecordSchema = z.object({
  id: z.string(),
  providerId: perpsProviderIdSchema,
  accountId: z.string(),
  action: z.enum(EXTERNAL_ACTION_KINDS),
  state: externalActionStateSchema,
  amount: z.string().optional(),
  asset: z.string().optional(),
  chainId: z.number().int().positive().optional(),
  providerRequestId: z.string().optional(),
  txHash: z.string().optional(),
  authorizationData: z.unknown().optional(),
  evidence: reviewEvidenceSchema.optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
}) satisfies z.ZodType<ExternalActionRecord>;

export const automatedReviewResultSchema = z.discriminatedUnion('result', [
  z.object({
    result: z.literal('resolved_success'),
    evidence: reviewEvidenceSchema,
  }),
  z.object({
    result: z.literal('resolved_failure'),
    evidence: reviewEvidenceSchema,
  }),
  z.object({
    result: z.literal('needs_manual_support'),
    evidence: reviewEvidenceSchema,
  }),
  z.object({
    result: z.literal('retry_recommended'),
    evidence: reviewEvidenceSchema,
  }),
  z.object({
    result: z.literal('insufficient_evidence'),
    evidence: reviewEvidenceSchema,
  }),
]) satisfies z.ZodType<AutomatedReviewResult>;

export const perpsDepositInputContractSchema = z.object({
  providerId: perpsProviderIdSchema,
  asset: z.string(),
  amountAtomic: z.string(),
  fromChain: z.number().int().positive().optional(),
}) satisfies z.ZodType<PerpsDepositInput>;

export const perpsDepositWithSwidgeInputContractSchema =
  perpsDepositInputContractSchema.extend({
    sourceAsset: z.string(),
    sourceChain: z.number().int().positive(),
  }) satisfies z.ZodType<PerpsDepositWithSwidgeInput>;

export const perpsWithdrawInputContractSchema = z.object({
  providerId: perpsProviderIdSchema,
  asset: z.string(),
  amountAtomic: z.string(),
  destinationAddress: z.string(),
  fromChain: z.number().int().positive().optional(),
}) satisfies z.ZodType<PerpsWithdrawInput>;

export const perpsUsdClassTransferInputContractSchema = z.object({
  providerId: z.literal('hyperliquid'),
  accountId: z.string(),
  asset: z.literal('USDC'),
  amount: z.string(),
  fromClass: providerBalanceClassSchema,
  toClass: providerBalanceClassSchema,
}) satisfies z.ZodType<PerpsUsdClassTransferInput>;

export const usdClassTransferActionRecordSchema = z.object({
  id: z.string(),
  providerId: z.literal('hyperliquid'),
  accountId: z.string(),
  action: z.literal('usd_class_transfer'),
  state: externalActionStateSchema,
  amount: z.string(),
  asset: z.literal('USDC'),
  internalMovement: internalProviderMovementSchema,
  providerRequestId: z.string().optional(),
  txHash: z.string().optional(),
  evidence: reviewEvidenceSchema.optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
}) satisfies z.ZodType<UsdClassTransferActionRecord>;

export const perpsActionOutputSchema = z.object({
  actionId: z.string(),
  state: externalActionStateSchema,
  providerId: perpsProviderIdSchema,
  supportReference: z.string().optional(),
}) satisfies z.ZodType<PerpsActionOutput>;
